import { useState, useMemo } from 'react'
import type { ReactNode } from 'react'
import { Stack, Group, SegmentedControl, Text, Badge } from '@mantine/core'
import { IconUserCog, IconCode, IconBriefcase } from '@tabler/icons-react'
import { useAuthStore } from '@/store/auth'
import { DevDashboard } from './DevDashboard'
import { PODashboard } from './PODashboard'
import { ManagerDashboard } from './ManagerDashboard'

type Perfil = 'manager' | 'dev' | 'po'

const PERFIL_ICONS: Record<Perfil, ReactNode> = {
  manager: <IconUserCog size={14} />,
  dev: <IconCode size={14} />,
  po: <IconBriefcase size={14} />,
}

const PERFIL_LABELS: Record<Perfil, string> = {
  manager: 'Executivo',
  dev: 'Técnico',
  po: 'Produto',
}

/**
 * Dashboard Router Component
 *
 * Roteia o user para o dashboard apropriado, derivando a persona do
 * permission set efetivo (RBAC v3/v4 — fonte canônica). Switcher de perfil
 * continua funcionando.
 *
 * Mapping (permission set):
 *   admin:full | chamados:approve | usuarios:manage → Manager (executive, 3 switchers)
 *   recursos:manage | instance:deploy                → Dev
 *   finops:read (e não-executive e não-dev)          → PO
 *   outros                                            → Manager fallback
 */
export function Dashboard() {
  const { user } = useAuthStore()

  const permSet = useMemo(() => {
    const tenantPerms = Object.values(user?.tenant_permissions ?? {}).flat()
    return new Set<string>(
      [...(user?.global_permissions ?? []), ...tenantPerms].map((p) => p.toLowerCase()),
    )
  }, [user])

  const isExecutive = useMemo(
    () =>
      permSet.has('admin:full') ||
      permSet.has('chamados:approve') ||
      permSet.has('usuarios:manage'),
    [permSet],
  )

  const availablePerfis: Perfil[] = useMemo(() => {
    if (isExecutive) return ['manager', 'dev', 'po']
    const list: Perfil[] = []
    const hasDev = permSet.has('recursos:manage') || permSet.has('instance:deploy')
    const hasPO = permSet.has('finops:read')
    if (hasDev) list.push('dev')
    if (hasPO && !hasDev) list.push('po')
    if (list.length === 0) list.push('manager') // viewer/suporte/outros
    return list
  }, [isExecutive, permSet])

  // Perfil default — primeiro disponível na ordem de prioridade executiva
  const defaultPerfil: Perfil = availablePerfis[0] ?? 'manager'

  const [perfil, setPerfil] = useState<Perfil>(defaultPerfil)

  const renderDashboard = () => {
    switch (perfil) {
      case 'dev': return <DevDashboard />
      case 'po': return <PODashboard />
      case 'manager': return <ManagerDashboard />
    }
  }

  return (
    <Stack gap="md">
      {availablePerfis.length > 1 && (
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <Text size="sm" c="dimmed" fw={500}>Perfil de visualização:</Text>
            <SegmentedControl
              size="xs"
              value={perfil}
              onChange={(v) => setPerfil(v as Perfil)}
              data={availablePerfis.map((p) => ({
                value: p,
                label: (
                  <Group gap={6} wrap="nowrap" px={4}>
                    {PERFIL_ICONS[p]}
                    <Text size="xs" fw={500}>{PERFIL_LABELS[p]}</Text>
                  </Group>
                ),
              }))}
            />
          </Group>
          {isExecutive && (
            <Badge size="xs" variant="light" color="blue">
              Modo admin — acesso a todas as visões
            </Badge>
          )}
        </Group>
      )}
      {renderDashboard()}
    </Stack>
  )
}
